import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Mail, Globe2, ShieldAlert } from 'lucide-react';
import Button from '../ui/Button';
import { API_PATH, request } from '../../config/api';
import Modal from '../ui/Modal';
import styles from '../../pages/AdminPage.module.css';

const EmailProviderSettings = () => {
    const [emailDomains, setEmailDomains] = useState([]);
    const [emailDomainsLoading, setEmailDomainsLoading] = useState(false);
    const [emailDomainsError, setEmailDomainsError] = useState('');
    const [newDomain, setNewDomain] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);

    const fetchEmailDomains = useCallback(async () => {
        setEmailDomainsLoading(true);
        setEmailDomainsError('');
        try {
            const res = await request(`${API_PATH}/superadmin/settings/email-domains`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setEmailDomainsError(data.error || 'Failed to load allowed email providers.');
                setEmailDomains([]);
                return;
            }
            setEmailDomains(data.domains || []);
        } catch (e) {
            setEmailDomainsError('Network error while loading email providers.');
            setEmailDomains([]);
        } finally {
            setEmailDomainsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmailDomains();
    }, [fetchEmailDomains]);

    const saveEmailDomains = async (domains) => {
        setEmailDomainsLoading(true);
        setEmailDomainsError('');
        try {
            const res = await request(`${API_PATH}/superadmin/settings/email-domains`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domains }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setEmailDomainsError(data.error || 'Failed to update providers.');
                return;
            }
            setEmailDomains(data.domains || []);
            setNewDomain('');
        } catch (e) {
            setEmailDomainsError('Network error while updating providers.');
        } finally {
            setEmailDomainsLoading(false);
        }
    };

    const handleAddDomain = async () => {
        const value = newDomain.trim().toLowerCase();
        if (!value) return;
        if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(value)) {
            setEmailDomainsError('Please enter a valid domain like company.com');
            return;
        }
        if (emailDomains.includes(value)) {
            setEmailDomainsError('This provider is already in the list.');
            return;
        }
        await saveEmailDomains([...emailDomains, value]);
    };

    const handleRemoveDomain = (domain) => {
        const next = emailDomains.filter((d) => d !== domain);
        if (next.length === 0) {
            setEmailDomainsError('At least one provider must remain. Add another before removing this one.');
            return;
        }
        setPendingDelete({ domain, next });
    };

    const confirmRemoveDomain = async () => {
        if (!pendingDelete) return;
        const { next } = pendingDelete;
        await saveEmailDomains(next);
        setPendingDelete(null);
    };

    const totalProviders = emailDomains.length;

    return (
        <section className={styles.section}>
            <div className={styles.sectionHeader}>
                <div className={styles.sectionHeaderLeft}>
                    <div className={styles.sectionIconBadge}>
                        <Mail size={16} />
                    </div>
                    <div>
                        <h2 className={styles.sectionTitle}>Allowed email providers</h2>
                        <p className={styles.sectionHint}>
                            Only emails from these domains can create admin accounts. Temporary and disposable inboxes are automatically blocked.
                        </p>
                    </div>
                </div>
                <div className={styles.sectionHeaderRight}>
                    <span className={styles.providersCountBadge}>
                        <Globe2 size={14} />
                        <span>{totalProviders || 'No'} provider{totalProviders === 1 ? '' : 's'}</span>
                    </span>
                </div>
            </div>
            {emailDomainsError && (
                <div className={styles.errorBanner}>
                    {emailDomainsError}
                </div>
            )}
            <div className={styles.emailDomainsCard}>
                <div className={styles.emailDomainsList}>
                    {emailDomainsLoading && emailDomains.length === 0 ? (
                        <div className={styles.loadingBlock}>
                            <Loader2 size={20} className={styles.spin} />
                            <span>Loading providers…</span>
                        </div>
                    ) : emailDomains.length === 0 ? (
                        <div className={styles.emptyProviders}>
                            <p className={styles.sectionHint}>
                                No providers configured yet. While this is empty, any non‑temporary email domain can sign up.
                            </p>
                            <p className={styles.sectionHint}>
                                Add at least one work domain (for example <strong>company.com</strong>) to restrict who can create accounts.
                            </p>
                        </div>
                    ) : (
                        <div className={styles.domainChips}>
                            {emailDomains.map((domain) => (
                                <button
                                    key={domain}
                                    type="button"
                                    className={styles.domainChip}
                                    onClick={() => handleRemoveDomain(domain)}
                                    disabled={emailDomainsLoading}
                                    title="Click to remove provider"
                                    aria-label={`Remove ${domain} from allowed providers`}
                                >
                                    <span className={styles.domainChipLabel}>{domain}</span>
                                    <X size={14} aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.emailDomainsForm}>
                    <input
                        type="text"
                        className={styles.domainInput}
                        placeholder="Add provider, e.g. company.com"
                        value={newDomain}
                        onChange={(e) => { setNewDomain(e.target.value); setEmailDomainsError(''); }}
                        disabled={emailDomainsLoading}
                        aria-label="Add email provider domain"
                    />
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddDomain}
                        disabled={emailDomainsLoading || !newDomain.trim()}
                    >
                        {emailDomainsLoading ? 'Saving…' : 'Add provider'}
                    </Button>
                </div>
                <p className={styles.sectionHint}>
                    Temporary inboxes like <strong>mailinator</strong> or <strong>yopmail</strong> are blocked automatically, even if you don&apos;t list them here.
                </p>
            </div>

            <Modal
                isOpen={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                title="Remove email provider?"
                size="sm"
            >
                {pendingDelete && (
                    <>
                        <div className={styles.confirmIconRow}>
                            <div className={styles.sectionIconBadge}>
                                <ShieldAlert size={18} />
                            </div>
                            <div>
                                <p className={styles.confirmText}>
                                    You&apos;re about to remove <strong>{pendingDelete.domain}</strong> from the allowed providers list.
                                </p>
                                <p className={styles.confirmSubtext}>
                                    New signups using this domain will be blocked, but existing admins with this email will continue to work.
                                </p>
                            </div>
                        </div>
                        <div className={styles.deleteModalFooter}>
                            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={confirmRemoveDomain}>
                                Remove provider
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </section>
    );
};

export default EmailProviderSettings;

